import React from 'react';
import { Link } from 'react-router-dom';
import MarketingHeader from '../../components/MarketingHeader';
import { useSocialAccounts, useSocialAnalytics, useSocialPosts } from '../../hooks/useSocial';
import { PrimaryButtonLink } from '../../styles/marketingSharedStyles';
import { Card, CardTitle, Grid, Item, List, Metric, MetricLabel, MetricValue, Page, Row, Subtle } from './socialStyles';

const SocialDashboardPage: React.FC = () => {
  const accounts = useSocialAccounts();
  const posts = useSocialPosts();
  const analytics = useSocialAnalytics();

  const postList = posts.data ?? [];
  const counts = {
    accounts: (accounts.data ?? []).length,
    scheduled: postList.filter((post) => post.workflow_status === 'scheduled').length,
    pendingApproval: postList.filter((post) => post.workflow_status === 'pending_approval').length,
    published: postList.filter((post) => post.workflow_status === 'published').length,
  };

  return (
    <Page>
      <MarketingHeader
        activeTab="social"
        rightSlot={<PrimaryButtonLink to="/marketing/social/composer">+ New Social Post</PrimaryButtonLink>}
      />

      <Grid>
        <Metric>
          <MetricLabel>Connected Accounts</MetricLabel>
          <MetricValue>{counts.accounts}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Scheduled Posts</MetricLabel>
          <MetricValue>{counts.scheduled}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Pending Approval</MetricLabel>
          <MetricValue>{counts.pendingApproval}</MetricValue>
        </Metric>
      </Grid>

      <Grid>
        <Metric>
          <MetricLabel>Total Impressions</MetricLabel>
          <MetricValue>{analytics.data?.totals.impressions.toLocaleString() ?? '0'}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Total Reach</MetricLabel>
          <MetricValue>{analytics.data?.totals.reach.toLocaleString() ?? '0'}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel>Total Engagement</MetricLabel>
          <MetricValue>
            {(
              (analytics.data?.totals.likes ?? 0) +
              (analytics.data?.totals.comments ?? 0) +
              (analytics.data?.totals.shares ?? 0) +
              (analytics.data?.totals.clicks ?? 0)
            ).toLocaleString()}
          </MetricValue>
        </Metric>
      </Grid>

      <Card>
        <CardTitle>Quick Actions</CardTitle>
        <Row>
          <PrimaryButtonLink to="/marketing/social/accounts">Manage Accounts</PrimaryButtonLink>
          <PrimaryButtonLink to="/marketing/social/approvals">Review Approvals</PrimaryButtonLink>
          <PrimaryButtonLink to="/marketing/social/calendar">Open Calendar</PrimaryButtonLink>
          <PrimaryButtonLink to="/marketing/social/library">Media Library</PrimaryButtonLink>
        </Row>
      </Card>

      <Card>
        <CardTitle>Recent Social Posts</CardTitle>
        {posts.isLoading ? (
          <Subtle>Loading social posts...</Subtle>
        ) : (
          <List>
            {postList.slice(0, 8).map((post) => (
              <Item key={post.id}>
                <Row>
                  <strong>{post.title || 'Untitled Post'}</strong>
                  <span>{post.workflow_status}</span>
                  <span>{post.scheduled_for ? new Date(post.scheduled_for).toLocaleString() : 'No schedule'}</span>
                  <Link to={`/marketing/social/composer/${post.id}`}>Open</Link>
                </Row>
              </Item>
            ))}
            {postList.length === 0 ? <Subtle>No social posts yet.</Subtle> : null}
          </List>
        )}
      </Card>
    </Page>
  );
};

export default SocialDashboardPage;
